import { useState } from "react";
import styled from "styled-components";

import { useAnswerRiddle, useRiddle } from "../api/hooks";
import { Modal } from "../components/Modal";
import { ErrorNotice, Loading, SectionLabel } from "../components/primitives";
import { countdownTo } from "../lib/format";

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${({ theme }) => theme.space(3)};
`;

const ResetChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-family: ${({ theme }) => theme.font.mono};
  font-size: 11px;
  color: ${({ theme }) => theme.color.text};
  padding: 5px 10px;
  border-radius: ${({ theme }) => theme.radius.pill};
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid ${({ theme }) => theme.color.border};

  &::before {
    content: "●";
    color: ${({ theme }) => theme.color.happiness};
  }
`;

// The riddle card — a "sealed envelope" with a faint watermark question mark.
const Envelope = styled.div`
  position: relative;
  overflow: hidden;
  padding: 22px;
  border-radius: ${({ theme }) => theme.radius.xl};
  border: 1px solid ${({ theme }) => theme.color.border};
  background: linear-gradient(
    180deg,
    ${({ theme }) => theme.color.surface} 0%,
    ${({ theme }) => theme.color.surfaceAlt} 100%
  );
`;

const Watermark = styled.div`
  position: absolute;
  right: -30px;
  top: -50px;
  font-size: 240px;
  font-weight: 800;
  line-height: 1;
  color: rgba(0, 152, 234, 0.05);
  font-family: ${({ theme }) => theme.font.ui};
  pointer-events: none;
`;

const EnvHead = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 14px;
  position: relative;
  z-index: 1;
`;

const IconBadge = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ theme }) => theme.color.accentSoft};
  border: 1px solid rgba(0, 152, 234, 0.3);
  color: ${({ theme }) => theme.color.accent};
  font-weight: 700;
`;

const EnvSub = styled.div`
  font-family: ${({ theme }) => theme.font.mono};
  font-size: 11px;
  letter-spacing: 0.03em;
  color: ${({ theme }) => theme.color.textDim};
`;

const EnvTitle = styled.div`
  font-size: 15px;
  font-weight: 600;
`;

const Question = styled.p`
  position: relative;
  z-index: 1;
  font-size: 19px;
  line-height: 1.45;
  font-weight: 500;
  letter-spacing: -0.3px;
  margin: 0;
  color: ${({ theme }) => theme.color.text};
`;

const Quote = styled.span`
  color: ${({ theme }) => theme.color.accent};
`;

const MetaRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  margin-top: 18px;
  font-family: ${({ theme }) => theme.font.mono};
  font-size: 11px;
  color: ${({ theme }) => theme.color.textDim};
`;

const Solver = styled.span`
  color: ${({ theme }) => theme.color.happiness};
`;

const Sep = styled.span`
  color: ${({ theme }) => theme.color.textFaint};
`;

const InputSection = styled.div`
  margin-top: ${({ theme }) => theme.space(4)};
`;

const InputWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 6px 6px 6px 16px;
  background: ${({ theme }) => theme.color.surface};
  border: 1px solid ${({ theme }) => theme.color.borderHi};
  border-radius: ${({ theme }) => theme.radius.lg};

  &:focus-within {
    border-color: ${({ theme }) => theme.color.accent};
  }
`;

const Prompt = styled.span`
  font-family: ${({ theme }) => theme.font.mono};
  color: ${({ theme }) => theme.color.accent};
  font-size: 14px;
`;

const BareInput = styled.input`
  flex: 1;
  min-width: 0;
  background: transparent;
  border: none;
  outline: none;
  color: ${({ theme }) => theme.color.text};
  font-size: 15px;
`;

const GuessBtn = styled.button`
  padding: 10px 14px;
  border-radius: ${({ theme }) => theme.radius.md};
  border: none;
  background: ${({ theme }) => theme.color.accent};
  color: #fff;
  font-weight: 600;
  font-size: 13px;
  white-space: nowrap;

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
`;

const Hint = styled.div`
  margin-top: 8px;
  padding: 0 6px;
  font-family: ${({ theme }) => theme.font.mono};
  font-size: 10px;
  color: ${({ theme }) => theme.color.textFaint};
`;

const Verdict = styled.div<{ $correct: boolean }>`
  margin-top: ${({ theme }) => theme.space(3)};
  padding: ${({ theme }) => theme.space(3)};
  border-radius: ${({ theme }) => theme.radius.md};
  border: 1px solid ${({ theme, $correct }) => ($correct ? theme.color.good : theme.color.bad)};
  background: ${({ $correct }) =>
    $correct ? "rgba(63, 207, 142, 0.1)" : "rgba(229, 103, 91, 0.1)"};
  font-size: 14px;
`;

export function RiddleModal({ onClose }: { onClose: () => void }) {
  const { data, isLoading, error } = useRiddle();
  const answer = useAnswerRiddle();
  const [guess, setGuess] = useState("");

  const result = answer.data;
  // Riddle is solved (today, by anyone in chat) or this user has no attempts.
  const locked =
    !!data && (data.solved || data.attemptsRemaining <= 0 || result?.correct === true);

  function submit() {
    const g = guess.trim();
    if (!g || answer.isPending) return;
    answer.mutate({ guess: g });
  }

  return (
    <Modal onClose={onClose}>
      <Header>
        <SectionLabel>// riddle · daily</SectionLabel>
        {data && <ResetChip>resets in {countdownTo(data.resetsAt)}</ResetChip>}
      </Header>

      {isLoading && <Loading label="fetching riddle…" />}
      {error && <ErrorNotice message={error.message} />}

      {data && (
        <>
          <Envelope>
            <Watermark>?</Watermark>
            <EnvHead>
              <IconBadge>?</IconBadge>
              <div>
                <EnvSub>guarding the stash</EnvSub>
                <EnvTitle>Rocky&rsquo;s Riddle #{data.riddleNumber}</EnvTitle>
              </div>
            </EnvHead>
            <Question>
              <Quote>&ldquo;</Quote>
              {data.question}
              <Quote>&rdquo;</Quote>
            </Question>
            <MetaRow>
              <span>
                <Solver>◈</Solver> +{data.rewardPoints} solver pts
              </span>
              <Sep>·</Sep>
              <span>
                {data.attemptsRemaining} attempt{data.attemptsRemaining === 1 ? "" : "s"} left
              </span>
              <Sep>·</Sep>
              <span>{data.solvedTodayCount} solved today</span>
            </MetaRow>
          </Envelope>

          {!locked && (
            <InputSection>
              <SectionLabel>// your answer</SectionLabel>
              <InputWrap>
                <Prompt>&gt;</Prompt>
                <BareInput
                  value={guess}
                  placeholder="your answer"
                  onChange={(e) => setGuess(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && submit()}
                  autoFocus
                />
                <GuessBtn disabled={!guess.trim() || answer.isPending} onClick={submit}>
                  {answer.isPending ? "grading…" : "guess →"}
                </GuessBtn>
              </InputWrap>
              <Hint>// graded semantically — close-enough counts</Hint>
              {answer.error && <ErrorNotice message={answer.error.message} />}
            </InputSection>
          )}

          {result && (
            <Verdict $correct={result.correct}>
              {result.correct ? "Correct! " : "Not quite. "}
              {result.reason}
              {result.correct && result.pointsAwarded != null && ` (+${result.pointsAwarded} pts)`}
            </Verdict>
          )}

          {locked && !result && data.solved && (
            <Verdict $correct>
              Today&rsquo;s riddle is already solved{data.solvedBy ? ` by ${data.solvedBy}` : ""}.
            </Verdict>
          )}

          {locked && !result && !data.solved && data.attemptsRemaining <= 0 && (
            <Verdict $correct={false}>No attempts left — come back after reset.</Verdict>
          )}
        </>
      )}
    </Modal>
  );
}
